import React from 'react';
import { RocketIcon, BotIcon, CheckIcon, CrossIcon } from './ui/IconComponents';
import { Page } from '../types';

interface PricingPageProps {
  setCurrentPage: (page: Page) => void;
}

interface FeatureRowProps {
  feature: string;
  free: React.ReactNode;
  pro: React.ReactNode;
}

// Fix: Typed FeatureRow as React.FC to allow the 'key' prop required for list rendering.
const FeatureRow: React.FC<FeatureRowProps> = ({ feature, free, pro }) => (
    <tr className="border-b border-slate-200 dark:border-zinc-800 last:border-b-0">
        <td className="py-4 px-2 sm:px-4 text-slate-600 dark:text-zinc-300 font-medium">{feature}</td>
        <td className="py-4 px-2 sm:px-4 text-center text-slate-600 dark:text-zinc-300">{free}</td>
        <td className="py-4 px-2 sm:px-4 text-center font-semibold text-slate-800 dark:text-zinc-100">{pro}</td>
    </tr>
);


const PricingPage: React.FC<PricingPageProps> = ({ setCurrentPage }) => {

  const handlePayment = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  const proPlanPaymentLink = 'https://selar.co/wampia';
  const freePlanSignupLink = 'https://forms.gle/TexSbEUAED4sY2x69';

  const features = [
    { feature: "Availability", free: "6 hours daily (Fixed hours)", pro: "24/7 always online" },
    { feature: "Reply Speed", free: "Slow replies (1–2 min delay)", pro: "Instant replies (human-like speed)" },
    { feature: "Tasks / Skills", free: "Limited to 4 basic tasks", pro: "Unlimited tasks & full capabilities" },
    { feature: "Personalization", free: "Semi-personalized (no name, no advanced tone)", pro: "Fully personalized name, tone, personality" },
    { feature: "Human-Like Ability", free: "Semi-robotic responses", pro: "Ultra-human, natural multi-step reasoning" },
    { feature: "Sales Support", free: "Basic FAQ responses only", pro: "Full sales agent: closing, follow-ups, lead qualification" },
    { feature: "Customer Service", free: "Answers simple questions", pro: "Handles complaints, tracking, troubleshooting, complex queries" },
    { feature: "Integrations (CRM, Google Sheets, APIs, etc.)", free: <CrossIcon />, pro: <CheckIcon /> },
    { feature: "Data Tracking", free: "Not available", pro: "Complete analytics + performance tracking" },
    { feature: "Admin Dashboard via Telegram", free: "Not available", pro: "Admin panel with full access to logs/data" },
    { feature: "Lead Follow-Up", free: "Not available", pro: "Automated follow-up, reminders, and reactivation" },
    { feature: "Review Collection", free: "Not available", pro: "Automated review & feedback collection" },
    { feature: "Automation Power", free: "Very limited", pro: "Full workflow automation with dynamic logic" },
    { feature: "Business Fit", free: "Testing only", pro: "Full replacement for customer service & sales staff" },
    { feature: "Branding", free: "“Powered by Wampia” watermark", pro: "No watermark — fully white-labeled" },
  ];

  return (
    <div className="animate-fadeIn">
      <div className="py-20">
        <div className="container mx-auto px-4">
            <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                One powerful AI employee. <span className="text-[#05F564]">Two simple plans.</span>
            </h1>
            <div className="mt-6 inline-block bg-green-100 dark:bg-[#05f564]/10 text-green-800 dark:text-[#66ffaa] font-semibold px-4 py-1.5 rounded-full text-base">
                🎉 Limited Time Offer — 50% Off The Pro Plan!
            </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch mb-20">
               {/* Free Plan Card */}
               <div className="glass-effect rounded-2xl p-8 flex flex-col border border-slate-200 dark:border-zinc-800">
                  <div className="flex items-center gap-4">
                      <BotIcon />
                      <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Free Plan</h3>
                  </div>
                  <p className="mt-4 text-slate-600 dark:text-zinc-300 flex-grow">A great way to test the waters. Get a semi-automated agent to handle basic questions for a few hours a day.</p>
                  <div className="mt-6">
                      <span className="text-4xl font-extrabold text-slate-900 dark:text-white">Free</span>
                  </div>
                  <button onClick={() => handlePayment(freePlanSignupLink)} className="mt-8 w-full py-3 px-6 rounded-lg font-bold bg-slate-200 dark:bg-zinc-800 text-slate-800 dark:text-zinc-100 hover:bg-slate-300 dark:hover:bg-zinc-700 transition-all">
                      Get Started
                  </button>
              </div>

              {/* Pro Plan Card */}
              <div className="relative glass-effect rounded-2xl p-8 flex flex-col border-2 border-[#05F564]/80 shadow-2xl lg:scale-105 bg-gradient-to-br from-white via-white to-[#05f564]/5 dark:from-black dark:via-black dark:to-[#05f564]/10">
                  <div className="absolute top-0 right-8 -translate-y-1/2 bg-[#05F564] text-black text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider shadow-md">
                      Most Popular
                  </div>
                   <div className="flex items-center gap-4">
                      <RocketIcon />
                      <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Pro Plan</h3>
                  </div>
                  <p className="mt-4 text-slate-600 dark:text-zinc-300 flex-grow">Your complete AI employee. Works 24/7 to handle sales, support, and marketing, fully integrated with your business.</p>
                  <div className="mt-6">
                      <span className="text-2xl font-medium text-slate-400 dark:text-zinc-500 line-through">₦66,000</span>
                      <div className="flex items-baseline mt-1">
                          <span className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white">₦33,000</span>
                          <span className="text-lg text-slate-500 dark:text-zinc-400 ml-1">/ month</span>
                      </div>
                  </div>
                  <button onClick={() => handlePayment(proPlanPaymentLink)} className="mt-8 w-full py-3 px-6 rounded-lg font-bold bg-[#05F564] text-black hover:bg-[#04d458] shadow-[0_0_20px_rgba(var(--primary-green-rgb),0.3)] transition-all">
                      Go Pro & Get 50% Off
                  </button>
              </div>
            </div>
            
            {/* Comparison Table */}
            <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-center text-slate-900 dark:text-white mb-2">Full Feature Comparison</h2>
                <p className="text-center text-slate-600 dark:text-zinc-400 mb-10">See what's included in each plan.</p>
                <div className="glass-effect rounded-2xl p-4 sm:p-8 border border-slate-200 dark:border-zinc-800 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-300 dark:border-zinc-700">
                          <th className="text-left font-bold text-lg text-slate-800 dark:text-zinc-100 pb-4 px-2 sm:px-4">Feature</th>
                          <th className="font-bold text-lg text-slate-800 dark:text-zinc-100 text-center pb-4 px-2 sm:px-4">Free</th>
                          <th className="font-bold text-lg text-slate-800 dark:text-zinc-100 text-center pb-4 px-2 sm:px-4">
                            <span className="flex items-center justify-center gap-2">
                                Pro <span className="hidden sm:inline-block text-xs bg-[#05F564] text-black px-2 py-0.5 rounded-full font-bold">RECOMMENDED</span>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {features.map(({ feature, free, pro }) => <FeatureRow key={feature} feature={feature} free={free} pro={pro} />)}
                      </tbody>
                    </table>
                </div>
            </div>

        </div>
      </div>
      
      {/* Final CTA Section */}
      <div className="py-24 bg-slate-100/70 dark:bg-zinc-950">
        <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Ready to hire your first AI employee?</h2>
                <p className="mt-4 text-zinc-600 dark:text-zinc-300 text-lg max-w-2xl mx-auto">
                    Stop losing customers and start scaling your business on autopilot. Go Pro and let your AI agent do the heavy lifting, 24/7.
                </p>
                <button onClick={() => handlePayment(proPlanPaymentLink)} className="mt-8 bg-[#05F564] text-black font-bold px-8 py-3 rounded-lg shadow-[0_0_20px_rgba(var(--primary-green-rgb),0.3)] hover:bg-[#04d458] transition-all transform hover:scale-105">
                    Upgrade to Pro Now
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;